import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Upload, Share, Lock, Calendar, Plus, Search, Filter, Eye, ExternalLink, Pill, Heart, AlertTriangle, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { PDFViewer } from './PDFViewer';
import { fhirDataStore, optimizeFHIRBundle } from '@/utils/fhirDataUtils';
import { useGoogleDrive } from '@/contexts/GoogleDriveProvider';

interface Document {
  id: string;
  name: string;
  type: 'lab_result' | 'prescription' | 'insurance' | 'vaccination' | 'imaging' | 'other';
  date: string;
  provider: string;
  size: string;
  shared: boolean;
  encrypted: boolean;
  googleDriveFileId?: string;
}

interface MedicalData {
  medications: Array<{
    id: string;
    name: string;
    dosage?: string;
    frequency?: string;
    status: string;
    prescriber?: string;
    date?: string;
  }>;
  conditions: Array<{
    id: string;
    name: string;
    status: string;
    category?: string;
    onsetDate?: string;
  }>;
  allergies: Array<{
    id: string;
    substance: string;
    reaction?: string;
    severity?: string;
    status: string;
  }>;
}

interface CareCircleMember {
  id: string;
  name: string;
  email: string;
  relationship: string;
  role: 'owner' | 'caregiver' | 'viewer';
}

interface DocumentWalletProps {
  selectedPatientId?: string;
  onPatientDataImported?: (patientId?: string) => void;
}

export const DocumentWallet = ({ selectedPatientId = 'me', onPatientDataImported }: DocumentWalletProps) => {
  const { toast } = useToast();
  const { isReady: isDriveReady, isAuthorized, uploadFile, getAccessToken, listFiles } = useGoogleDrive();
  
  console.log('[DocumentWallet] Component rendered', { isDriveReady, isAuthorized, selectedPatientId });
  const [medicalData, setMedicalData] = useState<MedicalData>({
    medications: [],
    conditions: [],
    allergies: []
  });

  const [documents, setDocuments] = useState<Document[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareSearchTerm, setShareSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<CareCircleMember[]>([]);
  const [careCircleMembers, setCareCircleMembers] = useState<CareCircleMember[]>([]);
  const [sharingHistory, setSharingHistory] = useState<Array<{
    id: string;
    documentName: string;
    sharedWithName: string;
    status: string;
    sharedAt: string;
  }>>([]);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    loadMedicalDataForPatient(selectedPatientId);
  }, [selectedPatientId]);

  // Load Care Circle members
  useEffect(() => {
    const loadCareCircle = async () => {
      try {
        const token = localStorage.getItem('bearer_token');
        if (!token) return;
        const response = await fetch('/api/care_circle_members', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.members) setCareCircleMembers(data.members);
        }
      } catch (error) {
        console.error('Error loading Care Circle members:', error);
      }
    };
    loadCareCircle();
  }, []);

  // Load sharing history
  useEffect(() => {
    const loadSharingHistory = async () => {
      try {
        const token = localStorage.getItem('bearer_token');
        if (!token) return;
        const response = await fetch('/api/sharing_history', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.shares) setSharingHistory(data.shares);
        }
      } catch (error) {
        console.error('Error loading sharing history:', error);
      }
    };
    loadSharingHistory();
  }, []);

  const loadDriveFiles = async () => {
    const files = await listFiles({ pageSize: 100 });
    const driveDocs: Document[] = (files || []).map((f) => ({
      id: f.id,
      name: f.name,
      type: 'other',
      date: f.modifiedTime ? f.modifiedTime.split('T')[0] : new Date().toISOString().split('T')[0],
      provider: 'Google Drive',
      size: f.size ? formatFileSize(Number(f.size)) : '—',
      shared: false,
      encrypted: true,
      googleDriveFileId: f.id,
    }));
    setDocuments((prev) => {
      const prevById = new Map(prev.map((d) => [d.id, d] as const));
      for (const d of driveDocs) prevById.set(d.id, d);
      return Array.from(prevById.values());
    });
  };

  useEffect(() => {
    const fetchDriveDocs = async () => {
      try {
        if (!isDriveReady) return;
        // Try silent token first if not authorized; if it fails, wait for user gesture
        if (!isAuthorized) {
          try {
            await getAccessToken({ interactive: false });
          } catch {
            return; // not authorized yet; skip listing until user grants
          }
        }
        await loadDriveFiles();
      } catch (e) {
        // Swallow listing errors to avoid noisy auth logs; UI can retry after user grants
      }
    };
    fetchDriveDocs();
  }, [isDriveReady, isAuthorized, listFiles, getAccessToken]);

  const loadMedicalDataForPatient = async (patientId: string) => {
    console.log('Loading medical data for patient:', patientId);
    
    if (patientId === 'me') {
      // For current user, we don't have FHIR data yet
      setMedicalData({ medications: [], conditions: [], allergies: [] });
      return;
    }

    // Load FHIR data from IndexedDB
    const bundleData = await fhirDataStore.getFHIRData(patientId);
    if (!bundleData) {
      console.log('No FHIR data found for patient:', patientId);
      setMedicalData({ medications: [], conditions: [], allergies: [] });
      return;
    }

    try {
      const bundle = bundleData;
      const medications: MedicalData['medications'] = [];
      const conditionsMap = new Map<string, MedicalData['conditions'][0]>();
      const allergies: MedicalData['allergies'] = [];

      bundle.entry?.forEach((entry: any) => {
        const resource = entry.resource;
        
        if (resource.resourceType === 'MedicationRequest') {
          medications.push({
            id: resource.id || 'unknown',
            name: resource.medicationReference?.display || 
                  resource.medicationCodeableConcept?.text || 
                  resource.medicationCodeableConcept?.coding?.[0]?.display || 
                  'Unknown Medication',
            dosage: resource.dosageInstruction?.[0]?.text,
            frequency: resource.dosageInstruction?.[0]?.timing?.repeat?.frequency ? 
                      `${resource.dosageInstruction[0].timing.repeat.frequency}x daily` : undefined,
            status: resource.status || 'unknown',
            prescriber: resource.requester?.display,
            date: resource.authoredOn
          });
        }
        
        if (resource.resourceType === 'Condition') {
          const conditionName = resource.code?.text || 
                               resource.code?.coding?.[0]?.display || 
                               'Unknown Condition';
          const status = resource.clinicalStatus?.coding?.[0]?.code || 'unknown';
          
          // Only include active conditions and deduplicate by name
          if (status === 'active') {
            const condition = {
              id: resource.id || 'unknown',
              name: conditionName,
              status: status,
              category: resource.category?.[0]?.coding?.[0]?.display,
              onsetDate: resource.onsetDateTime || resource.onsetPeriod?.start
            };
            
            // Use the condition name as the key to deduplicate
            if (!conditionsMap.has(conditionName)) {
              conditionsMap.set(conditionName, condition);
            }
          }
        }
        
        if (resource.resourceType === 'AllergyIntolerance') {
          allergies.push({
            id: resource.id || 'unknown',
            substance: resource.code?.text || 
                      resource.code?.coding?.[0]?.display || 
                      'Unknown Allergen',
            reaction: resource.reaction?.[0]?.manifestation?.[0]?.text ||
                     resource.reaction?.[0]?.manifestation?.[0]?.coding?.[0]?.display,
            severity: resource.reaction?.[0]?.severity,
            status: resource.clinicalStatus?.coding?.[0]?.code || 'active'
          });
        }
      });

      // Convert the Map back to an array
      const conditions = Array.from(conditionsMap.values());

      setMedicalData({ medications, conditions, allergies });
      console.log('Loaded medical data:', { medications, conditions, allergies });
      
    } catch (error) {
      console.error('Error parsing FHIR data:', error);
      setMedicalData({ medications: [], conditions: [], allergies: [] });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    await processFiles(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    processFiles(files);
  };

  const processFiles = async (files: File[]) => {
    setUploadingFiles(files);
    setIsProcessing(true);

    try {
      for (const file of files) {
        await processFile(file);
      }
      
      toast({
        title: "Files processed successfully",
        description: `${files.length} file(s) have been processed and added to your wallet.`,
      });
      
      setUploadDialogOpen(false);
      setUploadingFiles([]);
      
    } catch (error) {
      console.error('Error processing files:', error);
      toast({
        title: "Error processing files",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const processFile = async (file: File): Promise<void> => {
    console.log('Processing file:', file.name, 'Type:', file.type);

    // Check if it's a JSON file
    if (file.type === 'application/json' || file.name.endsWith('.json')) {
      try {
        const text = await file.text();
        const jsonData = JSON.parse(text);
        
        // Check if it's a FHIR Bundle
        if (jsonData.resourceType === 'Bundle') {
          console.log('Detected FHIR Bundle, importing...');
          await importFHIRBundle(jsonData, file.name);
          return;
        }
      } catch (error) {
        console.error('Error parsing JSON file:', error);
        // Fall through to regular document processing
      }
    }

    // Process as regular document
    await addRegularDocument(file);
  };

  const importFHIRBundle = async (bundle: any, fileName: string) => {
    try {
      const optimizedBundle = optimizeFHIRBundle(bundle);

      // Extract patient information
      const patients = optimizedBundle.entry
        ?.filter((e: any) => e.resource.resourceType === 'Patient')
        .map((e: any) => e.resource) || [];

      let patientId = 'imported';
      let patientName = 'Imported Patient';

      if (patients.length > 0) {
        const patient = patients[0];
        patientId = patient.id || 'imported';
        patientName = patient.name?.[0] ? 
          `${patient.name[0].given?.join(' ') || ''} ${patient.name[0].family || ''}`.trim() : 
          'Imported Patient';
      }

      // Store the optimized bundle in IndexedDB only (no localStorage for FHIR bundles)
      await fhirDataStore.storeFHIRData(patientId, optimizedBundle);
      // If this is for the current user, reload the medical data from IndexedDB.
      if (selectedPatientId === 'me' || selectedPatientId === patientId) {
        loadMedicalDataForPatient(patientId);
      }

      // Trigger refresh of family members list in parent component and auto-select the imported patient
      if (onPatientDataImported) {
        onPatientDataImported(patientId);
      }

      toast({
        title: "FHIR Bundle imported successfully",
        description: `Medical data for ${patientName} has been imported from ${fileName}`,
      });

      console.log(`FHIR Bundle imported for patient: ${patientId}`);
      
    } catch (error) {
      console.error('Error importing FHIR bundle:', error);
      throw new Error('Failed to import FHIR bundle');
    }
  };

  const addRegularDocument = async (file: File) => {
    // Upload the actual file to Google Drive first
    try {
      const result = await uploadFile({
        file,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
      });

      const driveId: string | undefined = result?.id;

      // Create a new document entry
      const newDocument: Document = {
        id: driveId || Date.now().toString(),
        name: file.name,
        type: getDocumentTypeFromFile(file),
        date: new Date().toISOString().split('T')[0],
        provider: 'Self',
        size: formatFileSize(file.size),
        shared: false,
        encrypted: true,
        googleDriveFileId: driveId,
      };

      setDocuments(prev => [...prev, newDocument]);
      console.log('Uploaded and added document:', newDocument);
      toast({ title: 'Uploaded to Google Drive', description: driveId ? `File ID: ${driveId}` : 'Upload complete' });
    } catch (error: any) {
      console.error('Drive upload failed:', error);
      toast({ title: 'Drive upload failed', description: error?.message || 'Unknown error', variant: 'destructive' });

      // Still add a local entry so the user sees it; can retry upload later
      const newDocument: Document = {
        id: Date.now().toString(),
        name: file.name,
        type: getDocumentTypeFromFile(file),
        date: new Date().toISOString().split('T')[0],
        provider: 'Self',
        size: formatFileSize(file.size),
        shared: false,
        encrypted: true,
      };
      setDocuments(prev => [...prev, newDocument]);
    }
  };

  const getDocumentTypeFromFile = (file: File): Document['type'] => {
    const name = file.name.toLowerCase();
    
    if (name.includes('lab') || name.includes('test') || name.includes('result')) {
      return 'lab_result';
    }
    if (name.includes('prescription') || name.includes('rx') || name.includes('medication')) {
      return 'prescription';
    }
    if (name.includes('insurance') || name.includes('card')) {
      return 'insurance';
    }
    if (name.includes('vaccination') || name.includes('vaccine') || name.includes('immunization')) {
      return 'vaccination';
    }
    if (name.includes('scan') || name.includes('xray') || name.includes('mri') || name.includes('ct')) {
      return 'imaging';
    }
    
    return 'other';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleViewDocument = (doc: Document) => {
    console.log('Viewing document:', doc.name);
    setSelectedDocument(doc);
    setPdfViewerOpen(true);
    // In a real app, this would open a PDF viewer or download the file
    toast({
      title: "Document Opened",
      description: `Opening ${doc.name} in viewer...`,
    });
  };

  const handleShareDocument = (doc: Document) => {
    console.log('Sharing document:', doc.name);
    setSelectedDocument(doc);
    setSelectedMembers([]);
    setShareSearchTerm('');
    setShareDialogOpen(true);
  };

  const handleAddMember = (member: CareCircleMember) => {
    if (!selectedMembers.find((m) => m.id === member.id)) {
      setSelectedMembers([...selectedMembers, member]);
    }
    setShareSearchTerm('');
  };

  const handleRemoveMember = (memberId: string) => {
    setSelectedMembers(selectedMembers.filter((m) => m.id !== memberId));
  };

  const handleAddDocumentClick = async () => {
    try {
      if (!isAuthorized) {
        // Call interactive request directly within the user gesture
        await getAccessToken({ interactive: true });
      } else {
        // If already authorized, try silent refresh (ignore failures)
        try { await getAccessToken({ interactive: false }); } catch {}
      }
      setUploadDialogOpen(true);
    } catch (e: any) {
      toast({ title: 'Google Drive authorization failed', description: e?.message || 'Please try again.', variant: 'destructive' });
    }
  };

  const handleDocumentsTabClick = async () => {
    console.log('[DocumentWallet] Documents tab clicked - handleDocumentsTabClick called');
    try {
      console.log('[DocumentWallet] Authorized?', isAuthorized);
      if (!isAuthorized) {
        console.log('[DocumentWallet] Not authorized, calling getAccessToken with interactive=true');
        await getAccessToken({ interactive: true });
      }
      await loadDriveFiles();
    } catch (error) {
      console.error('[DocumentWallet] Error in handleDocumentsTabClick:', error);
      // user canceled or blocked; do nothing
    }
  };

  // Removed Save to Drive – files are shown directly from Drive

  const handleSendShare = async () => {
    if (!selectedDocument || selectedMembers.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one Care Circle member",
        variant: "destructive",
      });
      return;
    }

    console.log(`Sharing ${selectedDocument.name} with ${selectedMembers.map(m => m.name).join(', ')}`);
    
    try {
      const token = localStorage.getItem('bearer_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/share_document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          documentId: selectedDocument.id,
          documentName: selectedDocument.name,
          documentType: selectedDocument.type,
          googleDriveFileId: selectedDocument.googleDriveFileId,
          sharedWithMembers: selectedMembers,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to share document');
      }

      // Update document shared status
      setDocuments(prev => prev.map(doc => 
        doc.id === selectedDocument.id 
          ? { ...doc, shared: true }
          : doc
      ));

      // Reload sharing history
      const historyResponse = await fetch('/api/sharing_history', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (historyResponse.ok) {
        const data = await historyResponse.json();
        if (data.shares) setSharingHistory(data.shares);
      }

      toast({
        title: "Document Shared",
        description: `${selectedDocument.name} has been shared with ${selectedMembers.length} member(s)`,
      });

    } catch (error) {
      console.error('Error sharing document:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to share document",
        variant: "destructive",
      });
    }

    setShareDialogOpen(false);
    setSelectedMembers([]);
    setShareSearchTerm('');
    setSelectedDocument(null);
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'lab_result': return '🔬';
      case 'prescription': return '💊';
      case 'insurance': return '📋';
      case 'vaccination': return '💉';
      case 'imaging': return '🩻';
      default: return '📄';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'lab_result': return 'bg-blue-100 text-blue-700';
      case 'prescription': return 'bg-green-100 text-green-700';
      case 'insurance': return 'bg-purple-100 text-purple-700';
      case 'vaccination': return 'bg-orange-100 text-orange-700';
      case 'imaging': return 'bg-pink-100 text-pink-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.provider.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || doc.type === selectedType;
    return matchesSearch && matchesType;
  });

  const filteredCareCircle = careCircleMembers.filter(
    (m) =>
      m.name.toLowerCase().includes(shareSearchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(shareSearchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'inactive': case 'completed': return 'bg-gray-100 text-gray-700';
      case 'severe': return 'bg-red-100 text-red-700';
      case 'moderate': return 'bg-yellow-100 text-yellow-700';
      case 'mild': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Health Wallet</h2>
          <p className="text-muted-foreground">
            Securely store and share your health documents
          </p>
        </div>
        <div>
          <Button className="flex items-center space-x-2" onClick={handleAddDocumentClick}>
            <Plus className="h-4 w-4" />
            <span>Add Document</span>
          </Button>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragOver 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className={`h-12 w-12 mx-auto mb-4 ${isDragOver ? 'text-primary' : 'text-gray-400'}`} />
                <p className="text-lg font-medium">
                  {isDragOver ? 'Drop files here' : 'Drop files here or click to browse'}
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  PDF, JPG, PNG files up to 10MB
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  JSON files with FHIR bundles will be automatically imported
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.json"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  disabled={isProcessing}
                />
                <Button 
                  variant="outline" 
                  onClick={() => document.getElementById('file-upload')?.click()}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Choose Files'}
                </Button>
                
                {uploadingFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium">Selected files:</p>
                    {uploadingFiles.map((file, index) => (
                      <div key={index} className="text-sm text-muted-foreground">
                        {file.name} ({formatFileSize(file.size)})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="medical-data" className="space-y-4">
        <TabsList>
          <TabsTrigger value="medical-data">Medical Data</TabsTrigger>
          <TabsTrigger value="documents" onClick={handleDocumentsTabClick}>Documents ({filteredDocuments.length})</TabsTrigger>
          <TabsTrigger value="auto-fill">Auto-fill Profiles</TabsTrigger>
          <TabsTrigger value="sharing">Sharing History</TabsTrigger>
        </TabsList>

        <TabsContent value="medical-data">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Current Medications */}
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Pill className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold">Current Medications</h3>
              </div>
              <div className="space-y-3">
                {medicalData.medications.length > 0 ? (
                  medicalData.medications.map((med) => (
                    <div key={med.id} className="p-3 rounded-lg border">
                      <h4 className="font-medium text-sm">{med.name}</h4>
                      {med.dosage && (
                        <p className="text-xs text-muted-foreground">{med.dosage}</p>
                      )}
                      {med.frequency && (
                        <p className="text-xs text-muted-foreground">{med.frequency}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <Badge className={getStatusColor(med.status)} variant="secondary">
                          {med.status}
                        </Badge>
                        {med.date && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(med.date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No medications found</p>
                )}
              </div>
            </Card>

            {/* Medical Conditions */}
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Heart className="h-5 w-5 text-red-600" />
                <h3 className="text-lg font-semibold">Medical Conditions</h3>
              </div>
              <div className="space-y-3">
                {medicalData.conditions.length > 0 ? (
                  medicalData.conditions.map((condition) => (
                    <div key={condition.id} className="p-3 rounded-lg border">
                      <h4 className="font-medium text-sm">{condition.name}</h4>
                      {condition.category && (
                        <p className="text-xs text-muted-foreground">{condition.category}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <Badge className={getStatusColor(condition.status)} variant="secondary">
                          {condition.status}
                        </Badge>
                        {condition.onsetDate && (
                          <span className="text-xs text-muted-foreground">
                            Since {new Date(condition.onsetDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No conditions found</p>
                )}
              </div>
            </Card>

            {/* Allergies */}
            <Card className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <h3 className="text-lg font-semibold">Allergies</h3>
              </div>
              <div className="space-y-3">
                {medicalData.allergies.length > 0 ? (
                  medicalData.allergies.map((allergy) => (
                    <div key={allergy.id} className="p-3 rounded-lg border">
                      <h4 className="font-medium text-sm">{allergy.substance}</h4>
                      {allergy.reaction && (
                        <p className="text-xs text-muted-foreground">Reaction: {allergy.reaction}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <Badge className={getStatusColor(allergy.status)} variant="secondary">
                          {allergy.status}
                        </Badge>
                        {allergy.severity && (
                          <Badge className={getStatusColor(allergy.severity)} variant="secondary">
                            {allergy.severity}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No allergies found</p>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          {/* Search and Filter */}
          <Card className="p-4 mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="lab_result">Lab Results</option>
                  <option value="prescription">Prescriptions</option>
                  <option value="insurance">Insurance</option>
                  <option value="vaccination">Vaccinations</option>
                  <option value="imaging">Imaging</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => (
              <Card key={doc.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getDocumentIcon(doc.type)}</span>
                    <div className="flex items-center space-x-1">
                      {doc.encrypted && <Lock className="h-3 w-3 text-green-600" />}
                      {doc.shared && <Share className="h-3 w-3 text-blue-600" />}
                    </div>
                  </div>
                  <Badge className={getTypeColor(doc.type)}>
                    {doc.type.replace('_', ' ')}
                  </Badge>
                </div>
                
                <h3 className="font-medium text-sm mb-2 line-clamp-2">{doc.name}</h3>
                
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(doc.date).toLocaleDateString()}</span>
                  </div>
                  <p>Provider: {doc.provider}</p>
                  <p>Size: {doc.size}</p>
                </div>
                
                <div className="flex space-x-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleViewDocument(doc)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    View
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleShareDocument(doc)}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Share
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="auto-fill">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Auto-fill Profiles</h3>
            <div className="space-y-4">
              <div className="p-4 rounded-lg border">
                <h4 className="font-medium">Personal Information</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Basic details for medical forms
                </p>
                <div className="text-xs text-muted-foreground">
                  Name, DOB, Address, Phone, Emergency Contact
                </div>
                <Button variant="outline" size="sm" className="mt-2">
                  Edit Profile
                </Button>
              </div>
              <div className="p-4 rounded-lg border">
                <h4 className="font-medium">Insurance Information</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Coverage details for billing
                </p>
                <div className="text-xs text-muted-foreground">
                  Policy Number, Group ID, Carrier
                </div>
                <Button variant="outline" size="sm" className="mt-2">
                  Edit Profile
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sharing">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Sharing Activity</h3>
            <div className="space-y-3">
              {sharingHistory.length > 0 ? (
                sharingHistory.map((share) => (
                  <div key={share.id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <h4 className="font-medium text-sm">{share.documentName} shared with {share.sharedWithName}</h4>
                      <p className="text-xs text-muted-foreground">
                        {new Date(share.sharedAt).toLocaleDateString()} at {new Date(share.sharedAt).toLocaleTimeString()}
                      </p>
                    </div>
                    <Badge variant="outline" className="capitalize">{share.status}</Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No sharing history yet. Share a document with Care Circle members to see activity here.
                </p>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Share "{selectedDocument?.name}" with Care Circle members:
              </p>
              {selectedMembers.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded-md text-sm"
                    >
                      <span>{member.name}</span>
                      <button onClick={() => handleRemoveMember(member.id)} className="hover:bg-primary/20 rounded">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Input
                placeholder="Search Care Circle members..."
                value={shareSearchTerm}
                onChange={(e) => setShareSearchTerm(e.target.value)}
              />
              {shareSearchTerm && filteredCareCircle.length > 0 && (
                <div className="mt-2 border rounded-md max-h-48 overflow-y-auto">
                  {filteredCareCircle.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => handleAddMember(member)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between"
                      disabled={selectedMembers.some((m) => m.id === member.id)}
                    >
                      <div>
                        <div className="font-medium text-sm">{member.name}</div>
                        <div className="text-xs text-muted-foreground">{member.relationship} · {member.email}</div>
                      </div>
                      {selectedMembers.some((m) => m.id === member.id) && (
                        <span className="text-xs text-muted-foreground">Selected</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendShare}>
                Share with {selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer */}
      <PDFViewer
        open={pdfViewerOpen}
        onOpenChange={setPdfViewerOpen}
        documentName={selectedDocument?.name || ''}
        driveWebViewLink={selectedDocument?.googleDriveFileId ? 
            'https://qr.md/r/sprz/?t=View&v=desktop&f='+`https://drive.google.com/file/d/${selectedDocument.googleDriveFileId}/view` : undefined}
      />
    </div>
  );
};
